// ============================================================================
// PartnerDocumentsSection — the AFB-0011 document repository surface for a
// subcontractor / partner. Renders the canonical doc types (W-9, COI, Signed
// Contract, Signed Proposal) plus a Direct-Deposit row, each with an
// "Add / update" Drive-URL control. URLs only — never a file upload, never a
// bank number.
//
// PII posture (HR PII policy §5 + 2026-05-28 sub-payment memo):
//   - Direct_Deposit is HR / superuser only. For everyone else the row is
//     hidden entirely (RLS also masks those rows), and the copy reads
//     "Vendor payment profile verified in rail" — TCS does not collect a DD
//     PDF on the default path.
//   - Writes are HR / superuser only (RLS-enforced); other roles see the
//     repository read-only.
//
// Mounted in onboarding-detail.tsx (partner records) and the subcontractors
// edit slide-over.
// ============================================================================
import { canHandleDirectDeposit, canWriteOnboarding } from '../../shared/role-gates';
import { useState } from 'react';
import { Check, ExternalLink, Loader2, X } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { Button, useToast } from '../../shared/components';
import { usePartnerDocuments } from './hooks/use-partner-documents';
import { partnerDocumentLabel } from './types';
import { COI_LIGHT_CLASS, COI_LIGHT_LABEL, coiTrafficLight, fmtDate } from './utils';
import { errorMessage } from '../../shared/errors';

// Writers of the non-sensitive partner docs (W-9, COI, signed contract /
// proposal): hr/superuser plus national_am (full AM authority).
// Direct Deposit is sensitive PII: hr/superuser ONLY. RLS also masks the
// Direct_Deposit row from every other role (national_am included), so this
// gate stays narrower than canWriteOnboarding on purpose — do not widen it.

// Canonical document types shown as fixed rows (Direct_Deposit handled below).
const CANONICAL: { type: string; label: string }[] = [
  { type: 'W9', label: 'W-9' },
  { type: 'COI', label: 'Certificate of Insurance (COI)' },
  { type: 'Signed_Contract', label: 'Signed contract' },
  // AFB-0050 — this link points to the Drive FOLDER where the signed BSOAs
  // (building service operating agreements) live, not a single file.
  { type: 'Signed_Proposal', label: 'Building Proposal Folder' },
];

// AFB-0050 — doc types whose drive_link is a Drive FOLDER (not a single
// file). Drives folder-appropriate copy ("Open folder" / "No folder linked").
const FOLDER_TYPES = new Set(['Signed_Proposal']);

interface Props {
  providerId: string | null;
  onboardingId?: string | null;
  /** Section heading. Defaults to "Documents"; the subcontractors edit
      slide-over passes "Documents & Compliance" (the compliance + documents
      sections were collapsed into this one surface, 2026-07-10). */
  title?: string;
}

export default function PartnerDocumentsSection({
  providerId,
  onboardingId,
  title = 'Documents',
}: Props) {
  const { profile, employee } = useAuth();
  const toast = useToast();
  const canWrite = canWriteOnboarding(profile);
  const isHr = canHandleDirectDeposit(profile); // Direct Deposit row only
  const { documents, loading, error, upsertDocument } =
    usePartnerDocuments(providerId);

  const [editingType, setEditingType] = useState<string | null>(null);
  const [draftUrl, setDraftUrl] = useState('');
  // COI expiry — it IS the COI state. v_provider_compliance_status derives
  // every screen's answer from this date; there is no mirror column any more.
  const [draftExp, setDraftExp] = useState('');
  const [saving, setSaving] = useState(false);

  if (!providerId) return null;

  const byType = new Map(documents.map((d) => [d.document_type, d]));

  async function save(type: string) {
    if (!draftUrl.trim()) return;
    setSaving(true);
    try {
      await upsertDocument({
        document_type: type,
        drive_link: draftUrl.trim(),
        onboarding_id: onboardingId ?? null,
        uploaded_by: employee?.employee_id ?? null,
        // The expiry is the whole answer: no date means 'No COI on file'.
        ...(type === 'COI' ? { expiration_date: draftExp || null } : {}),
      });
      toast.success('Document link saved');
      setEditingType(null);
      setDraftUrl('');
      setDraftExp('');
    } catch (e) {
      toast.error('Could not save document', {
        description: errorMessage(e, 'Permission denied.'),
      });
    } finally {
      setSaving(false);
    }
  }

  function startEdit(type: string, current: string | null | undefined) {
    setEditingType(type);
    setDraftUrl(current ?? '');
    setDraftExp(byType.get(type)?.expiration_date ?? '');
  }

  // Ad-hoc "Other" docs beyond the canonical set + Direct_Deposit.
  const otherDocs = documents.filter(
    (d) =>
      d.document_type !== 'Direct_Deposit' &&
      !CANONICAL.some((c) => c.type === d.document_type),
  );

  return (
    <section className="mb-5">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-secondary">
        {title}
      </h3>

      {error && (
        <div className="mb-2 rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
          {error}
        </div>
      )}
      {loading ? (
        <div className="rounded-md border border-tcs-border px-3 py-2 text-xs text-tcs-text-muted">
          Loading documents…
        </div>
      ) : (
        <ul className="space-y-1.5">
          {CANONICAL.map(({ type, label }) => {
            const doc = byType.get(type);
            const isEditing = editingType === type;
            const isFolder = FOLDER_TYPES.has(type);
            return (
              <li
                key={type}
                className="rounded-md border border-tcs-border px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm text-tcs-text">{label}</div>
                    {isFolder && (
                      <div className="mt-0.5 text-[11px] text-tcs-text-muted">
                        Drive folder where the signed BSOAs live.
                      </div>
                    )}
                    <div className="mt-0.5 flex items-center gap-2 text-xs">
                      {doc?.drive_link ? (
                        <a
                          href={doc.drive_link}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1 text-tcs-accent-ink hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />{' '}
                          {isFolder ? 'Open folder' : 'View'}
                        </a>
                      ) : (
                        <span className="text-tcs-text-muted">
                          {isFolder ? 'No folder linked' : 'Not on file'}
                        </span>
                      )}
                      {doc?.status && (
                        <span className="rounded-full bg-tcs-surface px-1.5 py-0.5 text-[10px] text-tcs-text-secondary">
                          {doc.status}
                        </span>
                      )}
                    </div>
                    {/* COI expiry + traffic light. An expired certificate reads
                        as 'Expired' and a missing date as 'No COI on file' —
                        v_provider_compliance_status draws that distinction, and
                        Antonela needs it to know which one to chase. */}
                    {type === 'COI' && doc?.drive_link && (
                      <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                        <span className={`inline-block h-2 w-2 rounded-full ${COI_LIGHT_CLASS[coiTrafficLight(doc.expiration_date)]}`} />
                        <span className="text-tcs-text-muted">
                          {COI_LIGHT_LABEL[coiTrafficLight(doc.expiration_date)]} · expires {fmtDate(doc.expiration_date)}
                        </span>
                      </div>
                    )}
                  </div>
                  {canWrite && !isEditing && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(type, doc?.drive_link)}
                    >
                      {doc?.drive_link ? 'Update' : 'Add'}
                    </Button>
                  )}
                </div>
                {canWrite && isEditing && (
                  <UrlEditor
                    value={draftUrl}
                    saving={saving}
                    onChange={setDraftUrl}
                    placeholder={
                      isFolder
                        ? 'https://drive.google.com/drive/folders/… (folder URL)'
                        : undefined
                    }
                    showExpiry={type === 'COI'}
                    expiry={draftExp}
                    onExpiryChange={setDraftExp}
                    onCancel={() => {
                      setEditingType(null);
                      setDraftUrl('');
                      setDraftExp('');
                    }}
                    onSave={() => void save(type)}
                  />
                )}
              </li>
            );
          })}

          {/* Direct Deposit — HR / superuser only. */}
          {isHr && (
            <li className="rounded-md border border-tcs-border bg-tcs-bg/40 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-tcs-text">{partnerDocumentLabel('Direct_Deposit')}</div>
                  <div className="mt-0.5 text-xs text-tcs-text-muted">
                    Vendor payment profile verified in rail. TCS does not collect
                    a DD form on the default path — only file a signed fallback
                    record here if one exists.
                  </div>
                  {byType.get('Direct_Deposit')?.drive_link && (
                    <a
                      href={byType.get('Direct_Deposit')!.drive_link!}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-1 inline-flex items-center gap-1 text-xs text-tcs-accent-ink hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" /> View fallback record
                    </a>
                  )}
                </div>
                {editingType !== 'Direct_Deposit' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      startEdit(
                        'Direct_Deposit',
                        byType.get('Direct_Deposit')?.drive_link,
                      )
                    }
                  >
                    {byType.get('Direct_Deposit')?.drive_link
                      ? 'Update'
                      : 'Add fallback'}
                  </Button>
                )}
              </div>
              {editingType === 'Direct_Deposit' && (
                <UrlEditor
                  value={draftUrl}
                  saving={saving}
                  onChange={setDraftUrl}
                  onCancel={() => {
                    setEditingType(null);
                    setDraftUrl('');
                  }}
                  onSave={() => void save('Direct_Deposit')}
                />
              )}
            </li>
          )}

          {otherDocs.map((d) => (
            <li
              key={d.document_id}
              className="rounded-md border border-tcs-border px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm text-tcs-text">
                  {d.document_name || partnerDocumentLabel(d.document_type)}
                </div>
                {d.drive_link && (
                  <a
                    href={d.drive_link}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-xs text-tcs-accent-ink hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" /> View
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {!canWrite && !loading && (
        <p className="mt-2 text-[11px] text-tcs-text-muted">
          Read-only — your role can view but not edit partner documents.
        </p>
      )}
    </section>
  );
}

function UrlEditor({
  value,
  saving,
  onChange,
  onCancel,
  onSave,
  placeholder = 'https://drive.google.com/… (URL only)',
  showExpiry = false,
  expiry = '',
  onExpiryChange,
}: {
  value: string;
  saving: boolean;
  onChange: (v: string) => void;
  onCancel: () => void;
  onSave: () => void;
  placeholder?: string;
  showExpiry?: boolean;
  expiry?: string;
  onExpiryChange?: (v: string) => void;
}) {
  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-xs"
        />
        <Button size="sm" disabled={saving || value.trim().length === 0} onClick={onSave}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} aria-label="Cancel">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      {showExpiry && (
        <label className="flex items-center gap-2 text-[11px] text-tcs-text-muted">
          COI expiration
          <input
            type="date"
            value={expiry}
            onChange={(e) => onExpiryChange?.(e.target.value)}
            className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1 text-xs"
          />
        </label>
      )}
    </div>
  );
}
